/**
 * L'emballage, vu par l'écran — le calcul, hors de React.
 *
 * Même découpe que `ot.ts`, `parc.ts` et `affectation.ts`, et pour la même
 * raison : ce qui est ici se teste sans navigateur, sans base et sans session.
 *
 * QUATRE CHOSES QUE CE FICHIER SAIT, ET QUE L'ÉCRAN N'A PAS À REFAIRE :
 *
 *   - **le solde d'un client est la somme de ses sites**, et un client peut
 *     devoir sur l'un tout en étant créditeur sur l'autre. Le total ne dit pas
 *     tout, c'est pourquoi la fiche a un onglet « Par site » ;
 *   - **le cumul se lit dans l'ordre du temps.** La colonne « solde après »
 *     n'a de sens que si les lignes se suivent. C'est la raison pour laquelle
 *     « par OT » est une COLONNE et non un regroupement ;
 *   - **un client sans échange affiche un TIRET, jamais zéro.** Un vraquier en
 *     benne n'échange pas de palette : un zéro y voudrait dire « à jour », ce
 *     qui est faux ;
 *   - **les palettes dues sans bon.** Un client peut nous devoir 28 palettes et
 *     n'avoir signé de papier que pour 23. Les cinq autres ne se réclament
 *     avec rien — et cela ne se voit dans aucun tableur.
 */

/** Un solde à la maille (client, site, support), tel que l'API le rend. */
export interface LigneSolde {
  readonly client_id: string;
  readonly client: string;
  readonly site_id: string;
  readonly site: string;
  readonly site_ville: string | null;
  readonly support: string;
  readonly ouverture: number;
  readonly livrees: number;
  readonly reprises: number;
  readonly solde: number;
  readonly dernier_jour: string | null;
  readonly mouvements: number;
}

/** Un bon, avec son état — déduit, sauf « perdu » qui est saisi. */
export interface Bon {
  readonly id: string;
  readonly numero: string;
  readonly client_id: string;
  readonly client: string;
  readonly site_id: string;
  readonly site: string;
  readonly support: string;
  readonly quantite: number;
  readonly emis_le: string;
  readonly expire_le: string | null;
  readonly ordre_origine: string | null;
  readonly ordre_recuperation: string | null;
  readonly perdu_le: string | null;
  readonly perdu_par: string | null;
  readonly note: string | null;
  readonly etat: string;
}

export interface MouvementEmballage {
  readonly id: string;
  readonly etape_id: string | null;
  readonly site_id: string;
  readonly site: string;
  readonly support: string;
  readonly livrees: number;
  readonly reprises: number;
  readonly nature: string;
  readonly jour: string;
  readonly bon_id: string | null;
  readonly bon_numero: string | null;
  readonly ot_id: string | null;
  readonly ot_numero: string | null;
  readonly note: string | null;
}

/** Une ligne du tableau principal : un client, tous supports et sites confondus. */
export interface LigneClient {
  readonly client_id: string;
  readonly client: string;
  readonly sites: number;
  readonly livrees: number;
  readonly reprises: number;
  readonly solde: number;
  readonly bonsEnAttente: number;
  readonly bonsExpires: number;
  readonly bonsPerdus: number;
  readonly dernierJour: string | null;
  /** Aucun mouvement ni ouverture : la ligne affiche des tirets, pas des zéros. */
  readonly vide: boolean;
}

// ---------------------------------------------------------------------------
// Les mots
// ---------------------------------------------------------------------------

/**
 * LE SIGNE PORTE LE SENS. La couleur ne fait que le répéter — le cadrage §7
 * interdit les aplats de couleur dans les lignes d'un tableau.
 */
export function sensDit(solde: number): string {
  if (solde > 0) return 'On nous doit';
  if (solde < 0) return 'Nous devons';
  return 'Soldé';
}

/** « +28 », « −12 », « 0 ». Le signe est écrit, y compris le plus. */
export function soldeDit(solde: number): string {
  if (solde > 0) return `+${String(solde)}`;
  if (solde < 0) return `−${String(Math.abs(solde))}`;
  return '0';
}

const ETATS_BON: Readonly<Record<string, string>> = {
  attente: 'En attente',
  expire: 'Expiré — à statuer',
  recupere: 'Récupéré',
  perdu: 'Perdu',
};

/**
 * « Expiré — à statuer » et non « Expiré » tout court.
 *
 * Ce n'est PAS un état de fin : le bon compte toujours dans le solde tant que
 * personne n'a tranché. « Un bon expiré doit être marqué par l'utilisateur
 * comme perdu, car parfois il y a un espoir. » Le libellé porte l'appel à
 * décision, sans quoi personne ne revient dans l'écran.
 */
export function etatBonDit(etat: string): string {
  return ETATS_BON[etat] ?? etat;
}

// ---------------------------------------------------------------------------
// Le tableau principal
// ---------------------------------------------------------------------------

/**
 * Les lignes du tableau principal : un client par ligne, ses sites comptés.
 *
 * Les bons sont pris ici plutôt que sur une seconde requête : les compter à
 * part obligerait l'écran à tenir deux listes d'accord, et un bon marqué perdu
 * ferait bouger l'une sans l'autre.
 */
export function parClient(
  soldes: readonly LigneSolde[],
  bons: readonly Bon[],
): readonly LigneClient[] {
  const par = new Map<string, {
    client: string;
    sites: Set<string>;
    livrees: number;
    reprises: number;
    solde: number;
    dernierJour: string | null;
    mouvements: number;
    ouverture: number;
  }>();

  for (const s of soldes) {
    const deja = par.get(s.client_id) ?? {
      client: s.client,
      sites: new Set<string>(),
      livrees: 0,
      reprises: 0,
      solde: 0,
      dernierJour: null,
      mouvements: 0,
      ouverture: 0,
    };
    deja.sites.add(s.site_id);
    deja.livrees += s.livrees;
    deja.reprises += s.reprises;
    deja.solde += s.solde;
    deja.mouvements += s.mouvements;
    deja.ouverture += s.ouverture;
    if (s.dernier_jour !== null && (deja.dernierJour === null || s.dernier_jour > deja.dernierJour)) {
      deja.dernierJour = s.dernier_jour;
    }
    par.set(s.client_id, deja);
  }

  // Un client peut n'avoir QUE des bons — aucun mouvement encore saisi. Il doit
  // apparaître : c'est précisément un compte qu'on n'a pas fini de traiter.
  for (const b of bons) {
    if (!par.has(b.client_id)) {
      par.set(b.client_id, {
        client: b.client,
        sites: new Set<string>(),
        livrees: 0,
        reprises: 0,
        solde: 0,
        dernierJour: null,
        mouvements: 0,
        ouverture: 0,
      });
    }
  }

  const lignes: LigneClient[] = [];
  for (const [id, v] of par) {
    const siens = bons.filter((b) => b.client_id === id);
    lignes.push({
      client_id: id,
      client: v.client,
      sites: v.sites.size,
      livrees: v.livrees,
      reprises: v.reprises,
      solde: v.solde,
      bonsEnAttente: siens.filter((b) => b.etat === 'attente').length,
      bonsExpires: siens.filter((b) => b.etat === 'expire').length,
      bonsPerdus: siens.filter((b) => b.etat === 'perdu').length,
      dernierJour: v.dernierJour,
      vide: v.mouvements === 0 && v.ouverture === 0,
    });
  }

  return lignes.sort((a, b) => a.client.localeCompare(b.client, 'fr'));
}

/**
 * « 6 clients — 2 nous doivent 68 palettes, 1 en attend 12 de nous · 3 bons
 * expirés à statuer ».
 *
 * LA SECONDE MOITIÉ EST LA RAISON D'ÊTRE DE L'ÉCRAN, comme le « 3 ordres
 * bloqués en facturation » des litiges : sans elle, la liste n'est qu'un
 * pense-bête. Avec elle, c'est du travail qui attend.
 */
export function decompteEmballage(lignes: readonly LigneClient[]): string {
  const total = lignes.length;
  const tete = total > 1 ? `${String(total)} clients` : `${String(total)} client`;

  const doivent = lignes.filter((l) => l.solde > 0);
  const dus = lignes.filter((l) => l.solde < 0);
  const expires = lignes.reduce((n, l) => n + l.bonsExpires, 0);

  const morceaux: string[] = [];
  if (doivent.length > 0) {
    const somme = doivent.reduce((n, l) => n + l.solde, 0);
    morceaux.push(
      `${String(doivent.length)} nous ${doivent.length > 1 ? 'doivent' : 'doit'} ${String(somme)} palettes`,
    );
  }
  if (dus.length > 0) {
    const somme = dus.reduce((n, l) => n + Math.abs(l.solde), 0);
    morceaux.push(
      `${String(dus.length)} en ${dus.length > 1 ? 'attendent' : 'attend'} ${String(somme)} de nous`,
    );
  }

  const debut = morceaux.length > 0 ? `${tete} — ${morceaux.join(', ')}` : tete;
  if (expires === 0) return debut;
  return `${debut} · ${String(expires)} bon${expires > 1 ? 's' : ''} expiré${expires > 1 ? 's' : ''} à statuer`;
}

/** Les quatre sens du filtre de l'écran principal. */
export type Sens = 'tout' | 'nous_doit' | 'nous_devons' | 'solde';

function aplatir(mot: string): string {
  return mot
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/**
 * Filtre local, comme les annuaires et la liste des ordres.
 *
 * Un client VIDE — aucun mouvement, aucune ouverture — n'est jamais « soldé » :
 * il n'a pas de compte, ce qui n'est pas la même chose qu'un compte à zéro.
 */
export function filtrerClients(
  lignes: readonly LigneClient[],
  recherche: string,
  sens: Sens,
): readonly LigneClient[] {
  const terme = aplatir(recherche.trim());

  return lignes.filter((l) => {
    if (terme !== '' && !aplatir(l.client).includes(terme)) return false;
    if (sens === 'nous_doit') return l.solde > 0;
    if (sens === 'nous_devons') return l.solde < 0;
    if (sens === 'solde') return l.solde === 0 && !l.vide;
    return true;
  });
}

// ---------------------------------------------------------------------------
// La fiche d'un client
// ---------------------------------------------------------------------------

export interface LigneMouvement extends MouvementEmballage {
  readonly ecart: number;
  readonly soldeApres: number;
}

/**
 * Le cumul, du plus ancien au plus récent, EN PARTANT DE L'OUVERTURE.
 *
 * Sans l'ouverture, la première ligne partirait de zéro et l'historique
 * raconterait qu'un client ne devait rien le jour de la mise en service —
 * ce qui est faux, et c'est tout l'objet de la table d'ouverture.
 *
 * Le cumul est fait TOUS SUPPORTS CONFONDUS : c'est ce que la maquette
 * affiche, et le détail par support est dans l'onglet « Par site ». Mélanger
 * des palettes 80×120 et des 100×120 dans un même total est assumé — le solde
 * d'un client est une créance, pas un inventaire.
 */
export function cumuler(
  mouvements: readonly MouvementEmballage[],
  ouverture: number,
): readonly LigneMouvement[] {
  let courant = ouverture;
  return [...mouvements]
    .sort((a, b) => (a.jour === b.jour ? 0 : a.jour < b.jour ? -1 : 1))
    .map((m) => {
      const ecart = m.livrees - m.reprises;
      courant += ecart;
      return { ...m, ecart, soldeApres: courant };
    });
}

/** Les trois mailles de l'onglet « Par période ». */
export type Maille = 'mois' | 'trimestre' | 'annee';

export interface Periode {
  readonly cle: string;
  readonly nom: string;
  readonly mouvements: number;
  readonly livrees: number;
  readonly reprises: number;
  readonly ecart: number;
  readonly soldeALaFin: number;
}

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function clePeriode(jour: string, maille: Maille): { cle: string; nom: string } {
  const annee = jour.slice(0, 4);
  const mois = Number(jour.slice(5, 7));
  if (maille === 'annee') return { cle: annee, nom: annee };
  if (maille === 'trimestre') {
    const t = Math.ceil(mois / 3);
    // « 1ᵉʳ » et non « 1ᵉ » : l'ordinal du premier s'écrit autrement en
    // français, et l'écran le montre à l'utilisateur.
    const rang = t === 1 ? '1ᵉʳ' : `${String(t)}ᵉ`;
    return { cle: `${annee}-T${String(t)}`, nom: `${rang} trimestre ${annee}` };
  }
  return {
    cle: jour.slice(0, 7),
    nom: `${MOIS[mois - 1] ?? jour.slice(5, 7)} ${annee}`,
  };
}

/**
 * Les périodes, de la plus ancienne à la plus récente.
 *
 * **LES PÉRIODES SANS MOUVEMENT NE SONT PAS LISTÉES.** Écrire « octobre : 0, 0,
 * 0 » pour un client qui n'a rien échangé ce mois-là remplirait le tableau de
 * lignes qui n'apprennent rien, et noierait les mois où il s'est passé quelque
 * chose. Le solde à la fin, lui, reste juste : il court d'une période à l'autre.
 */
export function parPeriode(
  mouvements: readonly MouvementEmballage[],
  ouverture: number,
  maille: Maille,
): readonly Periode[] {
  const par = new Map<string, { nom: string; n: number; livrees: number; reprises: number }>();

  for (const m of mouvements) {
    const { cle, nom } = clePeriode(m.jour, maille);
    const deja = par.get(cle) ?? { nom, n: 0, livrees: 0, reprises: 0 };
    deja.n += 1;
    deja.livrees += m.livrees;
    deja.reprises += m.reprises;
    par.set(cle, deja);
  }

  let courant = ouverture;
  return [...par.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([cle, v]) => {
      const ecart = v.livrees - v.reprises;
      courant += ecart;
      return {
        cle,
        nom: v.nom,
        mouvements: v.n,
        livrees: v.livrees,
        reprises: v.reprises,
        ecart,
        soldeALaFin: courant,
      };
    });
}

export interface Couverture {
  readonly solde: number;
  readonly couvertes: number;
  readonly perdues: number;
  readonly duesSansBon: number;
}

/**
 * « 23 couvertes · 12 perdues · 5 DUES SANS BON ».
 *
 * LA RAISON D'ÊTRE DE L'ONGLET, et elle tient en une phrase : cinq palettes
 * dues sans papier pour les réclamer. Cela ne se voit dans aucun tableur, et
 * c'est ce qui justifie l'écran à lui seul.
 *
 * Les bons RÉCUPÉRÉS ne comptent pas : leurs palettes sont revenues, et le
 * solde en tient déjà compte par le mouvement de reprise. Les PERDUS non plus
 * — le mouvement de perte a fait baisser le solde d'autant. Seuls les bons
 * encore vivants couvrent une créance.
 *
 * `duesSansBon` ne descend jamais sous zéro : plus de bons que de créance
 * signifie qu'on a des papiers pour des palettes déjà revenues, ce qui se
 * corrige en récupérant les bons, pas en affichant un nombre négatif.
 */
export function couverture(solde: number, bons: readonly Bon[]): Couverture {
  const vivants = bons.filter((b) => b.etat === 'attente' || b.etat === 'expire');
  const couvertes = vivants.reduce((n, b) => n + b.quantite, 0);
  const perdues = bons
    .filter((b) => b.etat === 'perdu')
    .reduce((n, b) => n + b.quantite, 0);

  return {
    solde,
    couvertes,
    perdues,
    duesSansBon: Math.max(0, solde - couvertes),
  };
}

/** « 23 couvertes · 12 perdues · 5 dues sans bon », en une ligne. */
export function couvertureDite(c: Couverture): string {
  const morceaux = [`${String(c.couvertes)} couverte${c.couvertes > 1 ? 's' : ''}`];
  if (c.perdues > 0) morceaux.push(`${String(c.perdues)} perdue${c.perdues > 1 ? 's' : ''}`);
  if (c.duesSansBon > 0) {
    morceaux.push(
      `${String(c.duesSansBon)} due${c.duesSansBon > 1 ? 's' : ''} sans bon`,
    );
  }
  return morceaux.join(' · ');
}
